import type { IRpcService } from "../rpc/types";
import type {
  GetCoPromotersForEntityResult,
  CreateCoPromotionResult,
  RevokeCoPromotionResult,
} from "api-contracts/rpc/co-promotion";

export interface ExistingCoPromoter {
  handle: string;
  coPromotionId: string;
}

export async function saveCollaborators(
  rpcService: IRpcService,
  entityType: "community" | "live_event",
  entityId: string,
  handles: string[],
  existingCoPromoters?: ExistingCoPromoter[],
): Promise<void> {
  let existingByHandle: Map<string, string>;

  if (existingCoPromoters) {
    existingByHandle = new Map(existingCoPromoters.map((cp) => [cp.handle, cp.coPromotionId]));
  } else {
    const existingResult = await rpcService.call<GetCoPromotersForEntityResult>("get-co-promoters-for-entity", {
      entityType,
      entityId,
    });
    existingByHandle = new Map(
      existingResult.coPromoters
        .filter((cp) => cp.promoterHandle !== null)
        .map((cp) => [cp.promoterHandle!, cp.coPromotionId]),
    );
  }

  const selectedSet = new Set(handles);

  const toCreate = handles.filter((h) => !existingByHandle.has(h));
  const toRevoke = [...existingByHandle.entries()]
    .filter(([handle]) => !selectedSet.has(handle))
    .map(([, coPromotionId]) => coPromotionId);

  const results = await Promise.allSettled([
    ...toCreate.map((handle) =>
      rpcService.call<CreateCoPromotionResult>("create-co-promotion", {
        entityType,
        entityId,
        promoterIdentifier: handle,
        identifierType: "handle",
      }),
    ),
    ...toRevoke.map((coPromotionId) =>
      rpcService.call<RevokeCoPromotionResult>("revoke-co-promotion", {
        coPromotionId,
      }),
    ),
  ]);

  const failures = results.filter((r) => r.status === "rejected");
  if (failures.length > 0) {
    console.warn(
      `[saveCollaborators] ${failures.length} of ${results.length} co-promotion operations failed:`,
      failures.map((f) => (f as PromiseRejectedResult).reason),
    );
  }
}
